/*
 * 版权: Copyright (c) 2018 red
 * 
 * 文件: buffer
 * 创建日期: Friday September 21st 2018
 * 作者: pao
 * 说明:
 * 1、缓冲区
 */

/**
 * 偏移位置
 */

export enum OffsetPosition {
    start,
    current,
    end
}

/**
 * 将数据缓存转换为字符串
 * @param data 数据缓存
 */
export function bufferToBase64(data: Buffer) {
    return data.toString('base64');
}

/**
 * 数据缓存
 * @param json json 字符串
 */
export function bufferFromBase64(json: string) {
    return Buffer.from(json, 'base64');
}
/**
 * 缓冲区大小，每次分配内存的大小
 */
const BufferSize = 1024;

/**
 * 缓冲区定位器
 */
export class BufferLocator {
    /**
     * 当前位置
     */
    protected position = 0;
    /**
     * 数据缓冲
     */
    protected buffer: Buffer;
    /**
     * Buffer的实际长度
     */
    protected bufferLength = 0;

    /**
     * 获取缓冲区大小
     */
    get bufferSize() {
        return this.bufferLength;
    }

    /**
     * 当前位置
     */
    get currentPosition() {
        return this.position;
    }

    /**
     * 是否已经读取完毕
     */
    get isEnd() {
        return this.position === this.bufferLength;
    }

    /**
     * 获取缓冲区
     */
    getBuffer(): Buffer {
        return this.buffer.slice(0, this.bufferLength);
    }

    /**
     * 位置偏移
     * @param startPos 启动位置
     * @param offset 偏移
     * @returns 偏移后的位置
     */
    offset(startPos: OffsetPosition, offset: number): number {
        switch (startPos) {
            case OffsetPosition.start:
                this.position = offset;
                break;
            case OffsetPosition.current:
                this.position += offset;
                break;
            case OffsetPosition.end:
                this.position = this.bufferLength + offset;
                break;
            default:
        }

        return this.position;
    }
}

/**
 * 缓冲区读取器
 */
export class BufferReader extends BufferLocator {
    constructor(buffer?: Buffer) {
        super();
        if (buffer) {
            this.buffer = buffer;
            this.bufferLength = buffer.length;
        }
    }

    /**
     * 读取字符串
     * @param encoding 编码格式
     * @returns 字符串
     */
    readString(encoding: string): string {
        let length = this.buffer.readInt32BE(this.position);
        let data = this.buffer.toString(encoding, this.position + 4, this.position + 4 + length);
        this.position += length + 4;
        return data;
    }

    /**
     * 读取数据缓冲 
     * @returns 数据缓冲
     */
    readBuffer(): Buffer {
        let length = this.buffer.readInt32BE(this.position);
        let data = Buffer.alloc(length);
        this.buffer.copy(data, 0, this.position + 4, this.position + 4 + length);
        this.position += length + 4;
        return data;
    }

    /**
     * 读取UInt8
     * @returns UInt8
     */
    readUInt8(): number {
        let data = this.buffer.readUInt8(this.position);
        this.position += 1;
        return data;
    }

    /**
     * 读取UInt16LE
     * @returns UInt16LE
     */
    readUInt16LE(): number {
        let data = this.buffer.readUInt16LE(this.position);
        this.position += 2;
        return data;
    }

    /**
     * 读取UInt16BE
     * @returns UInt16BE
     */
    readUInt16BE(): number {
        let data = this.buffer.readUInt16BE(this.position);
        this.position += 2;
        return data;
    }

    /**
     * 读取UInt32LE
     * @returns UInt32LE
     */
    readUInt32LE(): number {
        let data = this.buffer.readUInt32LE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取UInt32BE
     * @returns UInt32BE
     */
    readUInt32BE(): number {
        let data = this.buffer.readUInt32BE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取Int8
     * @returns Int8
     */
    readInt8(): number {
        let data = this.buffer.readInt8(this.position);
        this.position += 1;
        return data;
    }

    /**
     * 读取Int16LE
     * @returns Int16LE
     */
    readInt16LE(): number {
        let data = this.buffer.readInt16LE(this.position);
        this.position += 2;
        return data;
    }

    /**
     * 读取Int16BE
     * @returns Int16BE
     */
    readInt16BE(): number {
        let data = this.buffer.readInt16BE(this.position);
        this.position += 2;
        return data;
    }

    /**
     * 读取Int32LE
     * @returns Int32LE
     */
    readInt32LE(): number {
        let data = this.buffer.readInt32LE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取Int32BE
     * @returns Int32BE
     */
    readInt32BE(): number {
        let data = this.buffer.readInt32BE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取FloatLE
     * @returns FloatLE
     */
    readFloatLE(): number {
        let data = this.buffer.readFloatLE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取FloatBE
     * @returns FloatBE
     */
    readFloatBE(): number {
        let data = this.buffer.readFloatBE(this.position);
        this.position += 4;
        return data;
    }

    /**
     * 读取DoubleLE
     * @returns DoubleLE
     */
    readDoubleLE(): number {
        let data = this.buffer.readDoubleLE(this.position);
        this.position += 8;
        return data;
    }

    /**
     * 读取DoubleBE
     * @returns DoubleBE
     */
    readDoubleBE(): number {
        let data = this.buffer.readDoubleBE(this.position);
        this.position += 8;
        return data;
    }
}

/**
 * 写缓冲区的工具
 */
export class BufferWriter extends BufferLocator {
    constructor() {
        super();
        this.buffer = Buffer.alloc(BufferSize);
    }

    /**
     * 写入字符串
     * @param data 数据
     * @param encoding 编码格式
     * @returns 写入的字符串缓冲区大小
     */
    writeString(data: string, encoding?: string): number {
        let stringBufferSize = Buffer.byteLength(data, encoding);
        this.allocate(4 + stringBufferSize);

        this.buffer.writeInt32BE(stringBufferSize, this.position);
        this.buffer.write(data, this.position + 4, data.length, encoding);
        this.position += stringBufferSize + 4;
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return stringBufferSize;
    }

    /**
     * 写入数据缓存
     * @param data 数据
     * @returns 写入后的位置
     */
    writeBuffer(data: Buffer): number {
        this.allocate(4 + data.length);

        this.buffer.writeInt32BE(data.length, this.position);
        data.copy(this.buffer, this.position + 4, 0, data.length);
        this.position += data.length + 4;
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入UInt8
     * @param value UInt8
     * @returns 写入后的位置
     */
    writeUInt8(value: number): number {
        this.allocate(1);
        this.position = this.buffer.writeUInt8(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入UInt16LE
     * @param value UInt16LE
     * @returns 写入后的位置
     */
    writeUInt16LE(value: number): number {
        this.allocate(2);
        this.position = this.buffer.writeUInt16LE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入UInt16BE
     * @param value UInt16BE
     * @returns 写入后的位置
     */
    writeUInt16BE(value: number): number {
        this.allocate(2);
        this.position = this.buffer.writeUInt16BE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入UInt32LE
     * @param value UInt32LE
     * @returns 写入后的位置
     */
    writeUInt32LE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeUInt32LE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入UInt32BE
     * @param value UInt32BE
     * @returns 写入后的位置
     */
    writeUInt32BE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeUInt32BE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入Int8
     * @param value Int8
     * @returns 写入后的位置
     */
    writeInt8(value: number): number {
        this.allocate(1);
        this.position = this.buffer.writeInt8(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入Int16LE
     * @param value Int16LE
     * @returns 写入后的位置
     */
    writeInt16LE(value: number): number {
        this.allocate(2);
        this.position = this.buffer.writeInt16LE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入Int16BE
     * @param value Int16BE
     * @returns 写入后的位置
     */
    writeInt16BE(value: number): number {
        this.allocate(2);
        this.position = this.buffer.writeInt16BE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入Int32LE
     * @param value Int32LE
     * @returns 写入后的位置
     */
    writeInt32LE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeInt32LE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入Int32BE
     * @param value Int32BE
     * @returns 写入后的位置
     */
    writeInt32BE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeInt32BE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入FloatLE
     * @param value FloatLE
     * @returns 写入后的位置
     */
    writeFloatLE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeFloatLE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入FloatBE
     * @param value FloatBE
     * @returns 写入后的位置
     */
    writeFloatBE(value: number): number {
        this.allocate(4);
        this.position = this.buffer.writeFloatBE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入DoubleLE
     * @param value DoubleLE
     * @returns 写入后的位置
     */
    writeDoubleLE(value: number): number {
        this.allocate(8);
        this.position = this.buffer.writeDoubleLE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 写入DoubleBE
     * @param value DoubleBE
     * @returns 写入后的位置
     */
    writeDoubleBE(value: number): number {
        this.allocate(8);
        this.position = this.buffer.writeDoubleBE(value, this.position);
        this.bufferLength = Math.max(this.bufferLength, this.position);
        return this.position;
    }

    /**
     * 分配内存
     * @param needSize 需要的大小
     * @returns 如果新分配了内存，则返回True，否则，返回False
     */
    protected allocate(needSize: number): boolean {
        if (this.bufferLength + needSize > this.buffer.length) {
            // 扩大Buffer
            let newBuffer = Buffer.alloc(this.buffer.length + Math.max(BufferSize, needSize));
            this.buffer.copy(newBuffer);
            this.buffer = newBuffer;
            return true;
        }

        return false;
    }
}
